import {
    ABSOLUTE_NUMBERS_METRIC_DEFAULT_OPTION,
    ABSOLUTE_NUMBERS_METRIC_OPTIONS,
    PERCENT_CHANGE_OPTIONS,
} from "app/pages/sales-intelligence/sub-modules/signals/constants/traffic/options";
import type SignalCreateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalCreateDTO";
import type SignalUpdateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalUpdateDTO";
import TrafficChangesTrend from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesTrend";
import type { TrafficSignalOptionsConfig } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSIgnalOptions";
import TrafficSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalSubType";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";

import type Signal from "../core/Signal";
import type SignalFilters from "../core/SignalFilters";
import type SignalSource from "../core/SignalSource";
import SignalType from "../core/SignalType";
import type { SignalTemplateDeps } from "../templates/SignalTemplate";
import SignalTemplate from "../templates/SignalTemplate";

import type TrafficSignalFilters from "./TrafficSignalFilters";

type TrafficSignalTemplateDeps = Omit<SignalTemplateDeps<TrafficSignalFilters>, "signalType"> & {
    optionsConfig?: Readonly<TrafficSignalOptionsConfig>;
};

export default class TrafficSignalTemplate extends SignalTemplate<TrafficSignalFilters> {
    readonly optionsConfig?: Readonly<TrafficSignalOptionsConfig>;

    static defaultValues = {
        newMarkets: {
            thresholdValue: ABSOLUTE_NUMBERS_METRIC_DEFAULT_OPTION,
        },
    };

    static TRAFFIC_METRICS_THRESHOLD: readonly number[] = ABSOLUTE_NUMBERS_METRIC_OPTIONS;

    static TRAFFIC_CHANGE_VALUES: readonly number[] = PERCENT_CHANGE_OPTIONS;

    private directionToSignalSubType: Record<string, TrafficSignalSubType> = {
        [TrafficChangesTrend.Grew]: TrafficSignalSubType.GettingTraction,
        [TrafficChangesTrend.Declined]: TrafficSignalSubType.LosingTraction,
    };

    constructor({ optionsConfig, ...restDeps }: TrafficSignalTemplateDeps) {
        super({ ...restDeps, signalType: SignalType.Traffic });

        this.optionsConfig = optionsConfig;
    }

    private getSignalSubTypeByFilters(filters: TrafficSignalFilters): string | undefined {
        const isSubTypeMatchByDirectionFilter = Object.values(this.directionToSignalSubType).includes(
            this.signalSubType as TrafficSignalSubType,
        );

        if (isSubTypeMatchByDirectionFilter && isTrafficChangesModel(filters.trafficChanges)) {
            return this.directionToSignalSubType[filters.trafficChanges.direction];
        }

        return this.signalSubType;
    }

    private matchSignalByFilters(filters: TrafficSignalFilters): boolean {
        if (isTrafficChangesModel(filters.trafficChanges)) {
            const options = this.optionsConfig?.trafficChanges?.metrics;

            if (typeof options === "undefined") {
                return true;
            }

            return options.includes(filters.trafficChanges.metric);
        }

        return false;
    }

    isValid(): boolean {
        return typeof this.signalSubType !== "undefined";
    }

    matchesSignal(signal: Readonly<Signal<TrafficSignalFilters>>): boolean {
        const isSignalSubTypeMatched = signal.signalSubType === this.signalSubType;

        switch (signal.signalSubType) {
            case TrafficSignalSubType.GettingTraction:
            case TrafficSignalSubType.LosingTraction:
                return this.matchSignalByFilters(signal.filters);

            default:
                return isSignalSubTypeMatched;
        }
    }

    getSignalCreateDTO<F extends SignalFilters>(
        filters: Readonly<F>,
        signalSource: SignalSource,
    ): Readonly<SignalCreateDTO<F>> {
        return {
            isDefault: false,
            signalSource: signalSource,
            signalType: this.signalType,
            signalSubType: this.getSignalSubTypeByFilters(filters),
            lifecycleStatus: this.lifecycleStatus,
            filters,
        };
    }

    getSignalUpdateDTO<F extends SignalFilters>(
        uiSignal: Readonly<UISignal<F>>,
        filters: Readonly<F>,
    ): Readonly<SignalUpdateDTO<F>> {
        const { id, isDefault, signalType } = uiSignal;

        return {
            id,
            isDefault,
            signalType,
            signalSubType: this.getSignalSubTypeByFilters(filters),
            filters,
        };
    }
}
